import searchtabData from "@/public/data/searchTab.json";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Input from "@/shared/components/common/input/Input";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { cn } from "@/shared/libs/commonUtils";
import { Buildings, City } from "@phosphor-icons/react/dist/ssr";
import type React from "react";
import { type ElementType, useMemo, useState } from "react";
import LocationType from "./components/LocationType";
import CitySearch from "./components/CitySearch";
import AirportSearch from "./components/AirportSearch";
import BodySkeleton from "./components/BodySkeleton";

export type THotelSuggestion = {
  name: string;
  slug: string;
  city_iata: string;
  city_name?: string;
  discount_percent?: number | null;
};

type TLocation = "domestic" | "international";
export type TCity = {
  id: number;
  name: string;
  iata: string;
  lang_name: string;
  fa_name?: string;
};

interface LocationPickerProps {
  className?: string;
  OnCloseComp?: ElementType;
  formName?: "flight" | "hotel" | "tour" | "train" | "bus";
  onClick: (value: {
    name: string;
    id: string | number;
    type: string | undefined;
    code: string;
    cityIata?: string;
    hotelSlug?: string;
  }) => void;
  hotelSearch?: {
    results: THotelSuggestion[];
    loading: boolean;
    error: boolean;
  };
  route?: "origin" | "destination";
  searchResult: {
    data: TCity[] | undefined;
    isLoading: boolean;
    search: string;
    setSearch: React.ChangeEventHandler<HTMLInputElement> | undefined;
  };
}

export default function LocationPicker({
  className,
  formName = "flight",
  onClick,
  OnCloseComp,
  route,
  hotelSearch,
  searchResult: { data, isLoading, search, setSearch },
}: LocationPickerProps) {
  const locationData = searchtabData.tabs[formName].locationPicker;

  const {
    variants,
    search: searchPlaceholder,
    locations,
    noResult,
    title,
  } = locationData;

  const [locationType, setLocationType] = useState<TLocation>("domestic");
  const fallbackSearchResults = useMemo(
    () =>
      locations[locationType].filter((item) =>
        [
          item.name,
          item.lang_name,
          item.iata,
          ...(Array.isArray((item as AirportCity).airports)
            ? (item as AirportCity).airports.flatMap((airport) => [
                airport.name,
                airport.lang_name,
                airport.fa_name,
                airport.iata,
              ])
            : []),
        ].some((field) => normalizeLocationSearch(field).includes(normalizeLocationSearch(search))),
      ) as TCity[],
    [locationType, locations, search],
  );
  const searchResults = data?.length ? data : fallbackSearchResults;

  return (
    <Flex className={cn("p-0 flex-col", className)}>
      {/* Title & Type */}
      <Flex className="gap-x-2 py-4 px-4">
        {route && (
          <Typography className="font-medium">
            {typeof title !== "string" ? title[route] : title}
          </Typography>
        )}
        <Typography className="font-semibold">
          {variants.filter((vari) => vari.variant === locationType)[0].title}
        </Typography>
      </Flex>

      {/* Swtich type */}
      <LocationType
        variants={variants}
        locationType={locationType}
        onSelect={(location) => setLocationType(location)}
      />

      {/* Search input */}
      <Flex className="w-full px-4 py-3">
        <Input
          value={search || ""}
          placeholder={formName === "hotel" ? "جستجو نام شهر یا هتل" : searchPlaceholder}
          onChange={(e) => {
            if (setSearch) {
              setSearch(e);
              // Force re-render or ensure data is being fetched
            }
          }}
          autoFocus={false}
          className="rounded-xs"
        />
      </Flex>

      {isLoading ? (
        <BodySkeleton />
      ) : (
        <Flex className="w-full flex-col max-h-[400px] overflow-y-auto">
          {search && search.length > 0 ? (
            searchResults.length ? (
              searchResults.map((item, index) =>
                formName === "hotel" || formName === "tour" ? (
                  <CitySearch
                    key={`CitySearch-${index}`}
                    formName={formName}
                    city={item}
                    onClick={onClick}
                  />
                ) : (
                  <AirportSearch
                    key={`AirportSearch-${index}`}
                    airports={
                      item as AirportCity & { type: "city" | "airport" }
                    }
                    onClick={onClick}
                  />
                ),
              )
            ) : hotelSearch ? null : (
              <Flex className="flex-col w-full h-full border-b py-1 px-2  flex gap-3 items-center">
                <Flex className="w-full justify-center items-center gap-x-2">
                  <Typography className="font-medium">{noResult}</Typography>
                </Flex>
              </Flex>
            )
          ) : (
            locations[locationType].map(({ name, lang_name, id, iata }, z) => (
              <Flex
                className="flex-col w-full h-full border-b py-1 px-2 flex gap-x-3 items-center"
                key={`${lang_name}-${id}`}
              >
                <Flex className="w-full flex-col ">
                  <Flex
                    className="w-full justify-start items-center gap-x-2 cursor-pointer hover:bg-muted dark:hover:bg-muted/10 transition-all duration-150 py-2 rounded-xs px-1"
                    onClick={() =>
                      onClick({
                        name: lang_name,
                        id,
                        type: "city",
                        code: formName === "tour" ? name : iata,
                      })
                    }
                  >
                    <City className="text-primary" />
                    <Typography className="font-medium">{lang_name}</Typography>
                  </Flex>
                </Flex>
              </Flex>
            ))
          )}
        </Flex>
      )}

      {formName === "hotel" && hotelSearch && (
        <Flex className="w-full flex-col border-t border-border">
          <Typography className="sr-only">هتل‌ها</Typography>
          {(hotelSearch.loading ||
            hotelSearch.error ||
            !hotelSearch.results.length) && (
            <div role="status" className="px-4 py-2 text-sm text-muted-foreground">
              {hotelSearch.loading ? "در حال جستجوی هتل‌ها…" : hotelSearch.error
                ? "جستجوی هتل‌ها در دسترس نیست؛ می‌توانید شهر را انتخاب کنید."
                : "هتلی یافت نشد."}
            </div>
          )}
          <div className="max-h-[260px] overflow-y-auto">
            {hotelSearch.results.map((hotel) => {
              const city =
                data?.find((item) => item.iata === hotel.city_iata) ??
                locations.domestic.find((item) => item.iata === hotel.city_iata) ??
                locations.international.find(
                  (item) => item.iata === hotel.city_iata,
                );
              const cityName =
                hotel.city_name ?? city?.lang_name ?? hotel.city_iata;

              return (
                <button
                  key={hotel.slug}
                  type="button"
                  className="group flex w-full items-center gap-3 border-b border-border/70 px-4 py-3 text-start transition-colors last:border-b-0 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                  onClick={() => onClick({
                    name: hotel.name,
                    id: hotel.slug,
                    type: "hotel",
                    code: hotel.city_iata,
                    cityIata: hotel.city_iata,
                    hotelSlug: hotel.slug,
                  })}
                >
                  <Buildings className="size-5 shrink-0 text-muted-foreground group-hover:text-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {hotel.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {cityName}
                    </span>
                  </span>
                  {hotel.discount_percent ? (
                    <span className="shrink-0 rounded-sm bg-success-10 px-2 py-1 text-xs font-medium text-success">
                      تا {hotel.discount_percent}٪
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </Flex>
      )}

      {useDeviceType() === "mobile" && OnCloseComp && (
        <Flex className="w-full p-2 justify-end border-t">
          <OnCloseComp asChild>
            <Button className="rounded-xs text-xs  " variant="ghost">
              تایید
            </Button>
          </OnCloseComp>
        </Flex>
      )}
    </Flex>
  );
}

function normalizeLocationSearch(value?: string) {
  return (value ?? "").trim().toLowerCase();
}
